import { Card, CardContent, CardHeader, Chip, Fade, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useCurrentTenantInfoV2 } from "@/common/hooks/entity/tenant/useCurrentTenantInfoV2";
import { AppPermission, RoleDto } from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import AppLink from "../../Link/AppLink";
import RolePermissionList from "./RolePermissionList";

interface Props {
  role: RoleDto;
  withDetailsToggle?: boolean;
  isDetailsVisible?: boolean;
}

/** Displays role dto. */
export default function RoleListItem({
  role,
  withDetailsToggle = true,
  isDetailsVisible = false,
}: Props) {
  const tenantInfo = useCurrentTenantInfoV2();
  const authorizationInfo = useAuthorizationInfo();

  const [_isDetailsVisible, setIsDetailsVisible] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (isDetailsVisible !== undefined) {
      setIsDetailsVisible(isDetailsVisible);
    }
  }, [isDetailsVisible]);

  return (
    <Card>
      <CardHeader
        title={
          <Typography component='div' variant='subtitle1'>
            <AppLink
              sx={{ display: "inline" }}
              isEnabled={authorizationInfo.hasPermissionsAny([
                AppPermission.GlobalRoleRead,
                AppPermission.TenantRoleRead,
              ])}
              to={
                tenantInfo.isGlobalContext
                  ? ROUTE_PATH.ADMIN_ROLE_VIEW(role.id)
                  : ROUTE_PATH.TENANT_ROLE_VIEW(role.id)
              }
            >
              <span>{role.name}</span>
            </AppLink>

            {role.isBuiltIn && (
              <Tooltip title="Built-in roles are defined by the system and can't be changed.">
                <Chip
                  size='small'
                  variant='outlined'
                  color='primary'
                  label='Built-in'
                  sx={{ ml: 1 }}
                />
              </Tooltip>
            )}
          </Typography>
        }
        subheader={role.description}
        action={
          <>
            {withDetailsToggle && (
              <Tooltip title='View details'>
                <AppIconButton
                  onClick={() => setIsDetailsVisible(!_isDetailsVisible)}
                  sx={{ ml: "auto" }}
                >
                  {!_isDetailsVisible && <AppIcon of='expandMore' fontSize='medium' />}
                  {_isDetailsVisible && <AppIcon of='expandLess' fontSize='medium' />}
                </AppIconButton>
              </Tooltip>
            )}
          </>
        }
      />

      {/* Detailed view */}
      {_isDetailsVisible && (
        <Fade in={_isDetailsVisible}>
          <CardContent sx={{ pt: 0 }}>
            <Typography component='div' variant='h6' sx={{ mb: 1 }}>
              Permissions
            </Typography>

            <RolePermissionList role={role} />
          </CardContent>
        </Fade>
      )}
    </Card>
  );
}
